import React, { FC, useMemo } from 'react';
import { Avatar, Badge } from '@mui/material';
import { Image, Edit } from '@mui/icons-material';

import { imageMimes } from '@cvt/helpers/file';
import { FileUploader } from '@cvt/components/FileUploader';

interface Props {
  label: string;
  value?: string | File;
  disabled?: boolean;
  error?: boolean;
  onChange: (file: File) => void;
}

export const PictureUploader: FC<Props> = React.forwardRef(({ value, label, disabled, error, onChange }, ref: React.Ref<HTMLInputElement>) => {

  const photoUrl = useMemo(() => {
    if (!value) {
      return undefined;
    }

    return typeof value === 'string' ? value : URL.createObjectURL(value);
  }, [value]);

  return (
    <FileUploader
      {...ref}
      name="button-picture"
      label={label}
      disabled={disabled}
      inputProps={{
        accept: [...imageMimes].join(','),
        multiple: false,
      }}
      buttonRender={() => (
        <Badge
          overlap="circular"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          badgeContent={!disabled ? (
            <Avatar
              sx={theme => ({
                width: theme.spacing(3.5),
                height: theme.spacing(3.5),
                border: `3px solid ${theme.palette.common.white}`,
                padding: '3px',
                backgroundColor: theme.palette.secondary.dark,
                color: theme.palette.common.white,
              })}
            >
              <Edit color="inherit" style={{ width: '100%', height: '100%' }}/>
            </Avatar>
          ) : null}
        >
          <Avatar
            alt="Profile picture"
            src={photoUrl}
            sx={theme => ({
              width: 90,
              height: 90,
              border: '3px solid transparent',
              backgroundColor: theme.palette.background.default,
              borderColor: error ? theme.palette.error.main : 'transparent',
              color: theme.palette.secondary.dark,
              cursor: disabled || error ? 'default': 'pointer',
              '&:hover': {
                borderColor: disabled ? 'transparent' : error ? theme.palette.error.main : theme.palette.primary.main,
              },
            })}
          >
            <Image/>
          </Avatar>
        </Badge>
      )}
      onChange={files => onChange(files[0])}
    />
  );
});
